import React, { useState, useEffect } from 'react';
import { FaUsers, FaCrown, FaClock } from 'react-icons/fa';
import { RefreshCw } from 'lucide-react';

/* ================= TYPES ================= */

interface Seller {
  id: number;
  companyName: string; // backend se companyName aayega
  totalDurationMinutes: number; // Activity Time
  priority: number; // 0 = Auto, 1+ = Manual
  // Agar logo backend se nahi aa raha to placeholder use karenge
}

const BestDealerPage: React.FC = () => {
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Popup States
  const [showModal, setShowModal] = useState(false);
  const [selectedSeller, setSelectedSeller] = useState<Seller | null>(null);
  const [newPriority, setNewPriority] = useState(0);

  // --- 1. FETCH DATA ---
  const fetchSellers = async () => {
    setLoading(true);
    try {
      const res = await fetch('http://localhost:2027/api/admin/sellers-activity');
      if (res.ok) {
        const data = await res.json();
        setSellers(data);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchSellers(); }, []);

  // --- 2. UPDATE PRIORITY (API Call) ---
  const savePriority = async () => {
    if (!selectedSeller) return;

    try {
      // API call to save priority
      await fetch(`http://localhost:2027/api/admin/set-priority/${selectedSeller.id}?priority=${newPriority}`, {
        method: 'POST'
      });
      
      setShowModal(false);
      fetchSellers(); // List refresh karein taaki nayi sorting dikhe
    } catch (e) {
      alert("Failed to update priority");
    }
  };

  const openPopup = (seller: Seller) => {
    setSelectedSeller(seller);
    setNewPriority(seller.priority || 0); // Current priority set karein
    setShowModal(true);
  };

  // --- 3. SORTING LOGIC (Hybrid) ---
  const sortedSellers = [...sellers].sort((a, b) => {
    const pA = a.priority || 0;
    const pB = b.priority || 0;

    // Rule 1: Agar dono ki priority set hai (>0), to choti priority pehle (1, 2, 3)
    if (pA > 0 && pB > 0) return pA - pB;

    // Rule 2: Agar sirf A ki priority hai, to A upar
    if (pA > 0) return -1;

    // Rule 3: Agar sirf B ki priority hai, to B upar
    if (pB > 0) return 1;

    // Rule 4: Agar dono ki priority 0 hai (Auto Mode), to Time ke hisaab se (High to Low)
    return b.totalDurationMinutes - a.totalDurationMinutes;
  });

  // Top 5 for Cards
  const top5Sellers = sortedSellers.slice(0, 5);

  // Helper to format time
  const formatDuration = (mins: number) => {
     if(!mins) return "0m";
     const h = Math.floor(mins/60);
     const m = mins%60;
     return h>0 ? `${h}h ${m}m` : `${m}m`;
  };

  return (
    <div className="min-h-screen bg-gray-50 px-6 py-10">
      <div className="max-w-6xl mx-auto">

        {/* HEADER */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Top Dealers</h1>
            <p className="text-gray-500 text-sm">Sorted by Activity Time & Manual Priority</p>
          </div>
          <div className="flex gap-2">
             <button onClick={fetchSellers} className="bg-white p-3 rounded-xl shadow text-gray-600 hover:text-blue-600">
                <RefreshCw size={20} className={loading ? "animate-spin":""} />
             </button>
             <div className="flex items-center gap-2 bg-emerald-100 px-5 py-3 rounded-xl text-emerald-800 font-bold">
                <FaUsers /> Total: {sellers.length}
             </div>
          </div>
        </div>

        {/* TOP 5 CARDS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-10">
          {top5Sellers.map((seller, index) => (
            <div key={seller.id} className="bg-white rounded-3xl p-6 shadow-lg border border-emerald-50 relative overflow-hidden group hover:-translate-y-1 transition-all">
              
              {/* Rank Badge */}
              <div className="absolute top-0 right-0 bg-emerald-500 text-white px-3 py-1 rounded-bl-xl text-xs font-bold">
                 #{index + 1}
              </div>

              {/* Priority Indicator */}
              {seller.priority > 0 && (
                  <div className="absolute top-0 left-0 bg-yellow-400 text-yellow-900 px-2 py-1 rounded-br-lg text-[10px] font-bold flex items-center gap-1">
                      <FaCrown /> Pinned
                  </div>
              )}

              <div className="flex justify-center mb-4 mt-2">
                <div className="w-16 h-16 rounded-full bg-gradient-to-tr from-emerald-400 to-cyan-500 flex items-center justify-center text-white text-2xl font-bold shadow-md">
                    {seller.companyName?.charAt(0).toUpperCase()}
                </div>
              </div>

              <div className="text-center">
                <h3 className="font-bold text-gray-800 truncate" title={seller.companyName}>{seller.companyName}</h3>
                <p className="text-emerald-600 font-medium text-sm flex items-center justify-center gap-1 mt-1">
                   <FaClock size={12}/> {formatDuration(seller.totalDurationMinutes)} Active
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* FULL SELLER LIST */}
        <h3 className="text-xl font-bold text-gray-700 mb-4">All Sellers Management</h3>
        <div className="space-y-3">
          {sortedSellers.map((seller, index) => (
            <div
              key={seller.id}
              className={`border rounded-xl px-5 py-4 flex justify-between items-center transition-colors ${
                  seller.priority > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-white border-gray-200'
              }`}
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 font-bold">
                    {seller.companyName?.charAt(0)}
                </div>
                <div>
                  <h4 className="font-bold text-gray-800 flex items-center gap-2">
                      {seller.companyName}
                      {seller.priority > 0 && <FaCrown className="text-yellow-500 text-xs"/>}
                  </h4>
                  <p className="text-sm text-gray-500 flex items-center gap-3">
                    <span className="flex items-center gap-1"><FaClock size={12}/> {formatDuration(seller.totalDurationMinutes)}</span>
                    <span className="text-gray-300">|</span>
                    <span className={seller.priority > 0 ? "text-yellow-600 font-bold" : "text-gray-400"}>
                        {seller.priority > 0 ? `Manual Priority: ${seller.priority}` : "Auto Ranked"}
                    </span>
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                  <div className="text-2xl font-bold text-gray-200">#{index + 1}</div>
                  <button
                    onClick={() => openPopup(seller)}
                    className="bg-indigo-50 text-indigo-600 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-100 transition"
                  >
                    Set Priority
                  </button>
              </div>
            </div>
          ))}
        </div>

        {/* POPUP MODAL */}
        {showModal && selectedSeller && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
            <div className="bg-white rounded-2xl p-6 w-96 shadow-2xl transform scale-100 transition-all">
              <h3 className="text-xl font-bold mb-2 text-gray-800">
                Set Rank Priority
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                Assign a fixed position for <b>{selectedSeller.companyName}</b>. <br/>
                Set to <b>0</b> for automatic sorting based on time.
              </p>

              <label className="text-xs font-bold text-gray-500 uppercase">Priority Position</label>
              <select
                value={newPriority}
                onChange={e => setNewPriority(Number(e.target.value))}
                className="w-full border-2 border-indigo-100 rounded-xl p-3 mb-6 focus:outline-none focus:border-indigo-500 font-bold text-gray-700"
              >
                <option value={0}>Auto (Based on Activity)</option>
                <option value={1}>Rank #1 (Highest)</option>
                <option value={2}>Rank #2</option>
                <option value={3}>Rank #3</option>
                <option value={4}>Rank #4</option>
                <option value={5}>Rank #5</option>
                <option value={10}>Rank #10</option>
              </select>

              <div className="flex justify-end gap-3">
                <button 
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-gray-500 hover:bg-gray-100 rounded-lg font-medium"
                >
                    Cancel
                </button>
                <button
                  onClick={savePriority}
                  className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-indigo-700 shadow-lg shadow-indigo-200"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default BestDealerPage;